import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { Checkbox } from '@monorepo/shared/componentsV2/Checkbox';
import { ErrorIcon } from '@monorepo/shared/componentsV2/errorDisplays/ErrorIcon';
import { EditFieldValueFactory } from '@monorepo/shared/componentsV2/fieldDataType/values/EditFieldValueFactory';
import { fieldValueDisplayFactory } from '@monorepo/shared/componentsV2/fieldDataType/values/fieldValueDisplayFactory';
import { EditLogEntryBuiltInFields } from '@monorepo/shared/componentsV2/logs/EditLogEntryModal/EditLogEntryBuiltInFields';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import {
  ColumnSorting,
  TableColumn,
} from '@monorepo/shared/componentsV2/Table/types';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import AiSubmissionReviewedIcon from '@svg/ai-submission-reviewed.svg';
import { format } from 'date-fns';
import {
  FieldResponse,
  FieldValueResponse,
  localEquivalentOfUTC,
  LogFieldType,
  NumericFieldResponse,
} from 'mapistry-shared';
import { FieldDataType } from 'mapistry-shared/dist/consts/FieldDataType';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { LogEntryTableHeader } from './LogEntryTableHeader';

const StyledEditLogEntryBuiltInFields = styled(EditLogEntryBuiltInFields)`
  margin-top: 1.5rem;
  margin-bottom: 0.125rem;
`;

const StyledEditableCell = styled(EditFieldValueFactory)<{
  $isNumericFieldWithUnits: boolean;
}>`
  max-width: ${({ $isNumericFieldWithUnits }) =>
    $isNumericFieldWithUnits ? '15rem' : '10rem'};
  margin-top: 1.375rem;
`;

export const LOG_DATE_COLUMN_ID = 'log-date' as const;

const getValueForField = ({
  logFieldValue,
  fieldType,
}: {
  logFieldValue?: FieldValueResponse;
  fieldType: FieldDataType;
}) =>
  logFieldValue
    ? fieldValueDisplayFactory({
        fieldType,
        fieldValue: logFieldValue.fieldValue,
      })
    : '';

const statusIconTooltip = css`
  display: inline-flex;
  align-items: center;

  & svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const IncompleteEntryIndicator = styled(Tooltip)`
  ${statusIconTooltip}
`;

const AiSubmissionEntryIndicator = styled(Tooltip)`
  ${statusIconTooltip}
  margin-left: 0.25rem;

  & svg {
    fill: ${({ theme }) => theme.colors.builderBlue};
  }
`;

const AiSubmissionIconButton = styled(IconButton)`
  && {
    min-width: 0;
    height: auto;
    min-height: 0;
    padding: 0;
    font-size: 0.875rem;
    line-height: 0;
  }

  && svg {
    margin: 0;
  }
`;

const sortableLogFieldTypes: Array<FieldDataType> = [
  LogFieldType.BOOLEAN,
  LogFieldType.DATE,
  LogFieldType.DATETIME,
  LogFieldType.FORMULA,
  LogFieldType.NUMERIC,
  LogFieldType.RESOURCE,
  LogFieldType.SINGLE_SELECT,
  LogFieldType.TEXT,
  LogFieldType.TIME,
  LogFieldType.USER,
];

function getColumnSortingOptions(field: FieldResponse): ColumnSorting {
  const isSortable = sortableLogFieldTypes.includes(field.type);
  if (!isSortable) {
    return { isSortable: false };
  }
  return {
    isSortable: true,
    sortAriaHeader: field.name,
  };
}

// this column's definition is independent of any props, so don't recreate the object on each render
const incompleteEntryColumn: TableColumn<LogEntry> = {
  id: 'is-complete',
  header: <VisuallyHidden>Entry completion status</VisuallyHidden>,
  contents: ({ isComplete }) =>
    isComplete ? (
      <VisuallyHidden>The Log entry is complete</VisuallyHidden>
    ) : (
      <IncompleteEntryIndicator
        placement="bottom-start"
        title="This entry submission is missing required fields. Edit this row and complete all required fields to meet the requirement for this entry."
      >
        <div>
          <ErrorIcon />
          <VisuallyHidden>The Log entry is incomplete</VisuallyHidden>
        </div>
      </IncompleteEntryIndicator>
    ),
  width: '3%',
  padding: 'none',
};

const aiSubmissionEntryColumn = ({
  handleOpenExistingAiSubmissionsModal,
}: {
  handleOpenExistingAiSubmissionsModal: (
    edpAiSubmissionId: string,
    logEntryId: string,
  ) => void;
}): TableColumn<LogEntry> => ({
  id: 'is-ai-submission',
  header: <VisuallyHidden>AI submission status</VisuallyHidden>,
  contents: ({ id, edpAiSubmissionId }) =>
    edpAiSubmissionId ? (
      <AiSubmissionEntryIndicator
        placement="bottom-start"
        title="✨Submitted using AI assistance."
      >
        <div>
          <AiSubmissionIconButton
            onClick={() =>
              handleOpenExistingAiSubmissionsModal(edpAiSubmissionId, id)
            }
          >
            <AiSubmissionReviewedIcon aria-hidden />
            <VisuallyHidden>
              This log entry was submitted with AI.
            </VisuallyHidden>
          </AiSubmissionIconButton>
        </div>
      </AiSubmissionEntryIndicator>
    ) : (
      <VisuallyHidden>This log entry was not submitted with AI.</VisuallyHidden>
    ),
  width: '2%',
  padding: 'none',
});

export const useLogEntryTableColumns = ({
  logEntries,
  logEntryFields,
  isBulkActionsModeOn,
  isFieldDescriptionsShown,
  handleOpenExistingAiSubmissionsModal,
}: {
  logEntries: LogEntry[] | undefined;
  logEntryFields: FieldResponse[];
  isBulkActionsModeOn: boolean;
  isFieldDescriptionsShown: boolean;
  handleOpenExistingAiSubmissionsModal?: (
    edpAiSubmissionId: string,
    logEntryId: string,
  ) => void;
}): TableColumn<LogEntry>[] => {
  const hasIncompleteEntries = (logEntries || []).some(
    ({ isComplete }) => !isComplete,
  );
  const hasEdpAiSubmissionEntries = (logEntries || []).some(
    (logEntry) => logEntry.edpAiSubmissionId,
  );
  const columns: TableColumn<LogEntry>[] = useMemo(
    () => [
      ...(hasIncompleteEntries && !isBulkActionsModeOn
        ? [incompleteEntryColumn]
        : []),
      ...(hasEdpAiSubmissionEntries &&
      handleOpenExistingAiSubmissionsModal &&
      !isBulkActionsModeOn
        ? [aiSubmissionEntryColumn({ handleOpenExistingAiSubmissionsModal })]
        : []),
      {
        id: LOG_DATE_COLUMN_ID,
        header: 'Log Date',
        contents: ({ logDate }) =>
          format(localEquivalentOfUTC(new Date(logDate)), 'Pp'),
        isSortable: true,
        sortAriaHeader: 'Log Date',
      },
      ...logEntryFields.map<TableColumn<LogEntry>>((field) => ({
        id: field.columnName,
        header: LogEntryTableHeader({
          label: field.name,
          isFieldDescriptionsShown,
          fieldDescription: field.description,
        }),
        contents: ({ logFieldValues }) =>
          getValueForField({
            logFieldValue: logFieldValues[field.id],
            fieldType: field.type,
          }),
        ...getColumnSortingOptions(field),
      })),
    ],
    [
      handleOpenExistingAiSubmissionsModal,
      hasEdpAiSubmissionEntries,
      hasIncompleteEntries,
      isBulkActionsModeOn,
      isFieldDescriptionsShown,
      logEntryFields,
    ],
  );
  return columns;
};

export const useEditableLogEntryTableColumns = ({
  organizationId,
  projectId,
  logEntryFields,
  forceShowErrors,
  logEntryIDsToDelete = [],
  setLogEntryIDsToDelete,
}: {
  organizationId: string;
  projectId: string;
  logEntryFields: FieldResponse[];
  forceShowErrors?: boolean;
  logEntryIDsToDelete: string[];
  setLogEntryIDsToDelete: (logEntries: string[]) => void;
}): TableColumn<LogEntry>[] => {
  const columns: TableColumn<LogEntry>[] = useMemo(
    () => [
      {
        id: 'selection-controls',
        header: <div />,
        padding: 'none',
        contents: (logEntry) => (
          <Checkbox
            label={`remove-entry-${logEntry.id}`}
            isLabelHidden
            aria-label="Remove entry"
            checked={logEntryIDsToDelete.includes(logEntry.id)}
            onClick={() => {
              const l = logEntryIDsToDelete.slice();
              if (l.includes(logEntry.id)) {
                setLogEntryIDsToDelete(
                  logEntryIDsToDelete.filter((id) => id !== logEntry.id),
                );
                return;
              }
              setLogEntryIDsToDelete([...l, logEntry.id]);
            }}
          />
        ),
      },
      {
        id: LOG_DATE_COLUMN_ID,
        header: 'Log Date *',
        contents: (logEntry) => (
          <StyledEditLogEntryBuiltInFields
            existingLogEntry={logEntry}
            fieldNamePrefix={logEntry.id}
            isLabelHidden
            isCompact
            isErrorTextHidden={false}
            forceShowErrors={forceShowErrors}
          />
        ),
      },
      ...logEntryFields.map<TableColumn<LogEntry>>((field) => ({
        id: field.columnName,
        header: LogEntryTableHeader({
          label: field.name + (field.isRequired ? ' *' : ''),
          isFieldDescriptionsShown: false,
          fieldDescription: field.description,
        }),
        contents: ({ id, logFieldValues }) => (
          <StyledEditableCell
            field={field}
            fieldValue={
              logFieldValues ? logFieldValues[field.id]?.fieldValue : undefined
            }
            nameOverride={`${id}.${field.id}`}
            organizationId={organizationId}
            projectId={projectId}
            isLabelHidden
            isCompact
            isSubtextHidden
            forceShowErrors={forceShowErrors}
            $isNumericFieldWithUnits={
              field.type === LogFieldType.NUMERIC &&
              (field as NumericFieldResponse).units !== null
            }
          />
        ),
      })),
    ],
    [
      forceShowErrors,
      logEntryFields,
      logEntryIDsToDelete,
      organizationId,
      projectId,
      setLogEntryIDsToDelete,
    ],
  );
  return columns;
};
